import React from 'react';
import './FirstRepo.css';

function FirstRepo() {
  return (
    <section id="primer-repo" className="first-repo">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">Pas 4</span>
          <h2 className="section-title">Primer repositori <span className="gold">pas a pas</span></h2>
          <p className="section-desc">
            Segueix aquest tutorial per crear el teu primer repositori Git,
            connectar-lo a GitHub i fer el teu primer push.
          </p>
        </div>

        {/* Step 1: Create project */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">1</div>
            <div>
              <h3>Crear el projecte</h3>
              <p>Obre el terminal (Git Bash a Windows) i crea una carpeta per al projecte.</p>
            </div>
          </div>
          <div className="tut-terminal">
            <div className="terminal-header">
              <span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span>
              <span className="terminal-title">Git Bash</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line comment"># Crear la carpeta del projecte</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">mkdir el-meu-projecte</span></div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">cd el-meu-projecte</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Inicialitzar Git a la carpeta</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git init</span></div>
              <div className="terminal-line output success">Initialized empty Git repository in /el-meu-projecte/.git/</div>
            </div>
          </div>
          <div className="tut-explain">
            <strong>Que ha passat?</strong> Git ha creat una carpeta oculta <code>.git/</code> dins del teu projecte. Aqui es on Git emmagatzema tot l'historial. <strong>Mai toquis aquesta carpeta manualment!</strong>
          </div>
        </div>

        {/* Step 2: Create files */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">2</div>
            <div>
              <h3>Crear fitxers</h3>
              <p>Obre la carpeta a VS Code i crea alguns fitxers.</p>
            </div>
          </div>
          <div className="tut-terminal">
            <div className="terminal-header">
              <span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span>
              <span className="terminal-title">terminal</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line comment"># Obrir el projecte a VS Code</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">code .</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Crear un fitxer index.html (des del terminal o VS Code)</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">echo "&lt;h1&gt;Hola Git!&lt;/h1&gt;" &gt; index.html</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Veure l'estat dels fitxers</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git status</span></div>
              <div className="terminal-line output">On branch main</div>
              <div className="terminal-line output">No commits yet</div>
              <div className="terminal-line output"></div>
              <div className="terminal-line output danger">Untracked files:</div>
              <div className="terminal-line output danger">  index.html</div>
            </div>
          </div>
          <div className="tut-explain">
            <strong>Untracked</strong> vol dir que Git veu el fitxer pero <strong>encara no el segueix</strong>. Hem d'afegir-lo a la staging area.
          </div>
        </div>

        {/* Step 3: git add */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">3</div>
            <div>
              <h3>Afegir fitxers a la Staging Area</h3>
              <p>Seleccionem quins fitxers volem incloure al proper commit.</p>
            </div>
          </div>
          <div className="tut-terminal">
            <div className="terminal-header">
              <span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span>
              <span className="terminal-title">terminal</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line comment"># Afegir UN fitxer concret</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git add index.html</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># O afegir TOTS els fitxers nous/modificats</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git add .</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Verificar que s'ha afegit correctament</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git status</span></div>
              <div className="terminal-line output success">Changes to be committed:</div>
              <div className="terminal-line output success">  new file:   index.html</div>
            </div>
          </div>
          <div className="tut-explain">
            Ara el fitxer esta a la <strong>staging area</strong> (en verd). Aixo vol dir que esta <strong>preparat per ser commitejat</strong>.
          </div>
          <div className="tut-visual">
            <div className="visual-flow">
              <div className="flow-box working">
                <span className="flow-label">Working Directory</span>
                <div className="flow-file">index.html</div>
              </div>
              <div className="flow-arrow">
                <span>git add</span>
                <svg width="40" height="20" viewBox="0 0 40 20"><path d="M0 10h32M32 10l-6-5M32 10l-6 5" stroke="var(--gold)" strokeWidth="2" fill="none"/></svg>
              </div>
              <div className="flow-box staging">
                <span className="flow-label">Staging Area</span>
                <div className="flow-file">index.html</div>
              </div>
              <div className="flow-arrow">
                <span>git commit</span>
                <svg width="40" height="20" viewBox="0 0 40 20"><path d="M0 10h32M32 10l-6-5M32 10l-6 5" stroke="var(--gray-300)" strokeWidth="2" fill="none" strokeDasharray="4"/></svg>
              </div>
              <div className="flow-box repo inactive">
                <span className="flow-label">Repository</span>
                <div className="flow-file empty">buit</div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: git commit */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">4</div>
            <div>
              <h3>Fer el primer commit</h3>
              <p>Un commit es com una "foto" del teu projecte en un moment concret. Cada commit necessita un missatge descriptiu.</p>
            </div>
          </div>
          <div className="tut-terminal">
            <div className="terminal-header">
              <span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span>
              <span className="terminal-title">terminal</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git commit -m "Crear pagina principal amb titol"</span></div>
              <div className="terminal-line output success">[main (root-commit) a3f7b21] Crear pagina principal amb titol</div>
              <div className="terminal-line output"> 1 file changed, 1 insertion(+)</div>
              <div className="terminal-line output"> create mode 100644 index.html</div>
            </div>
          </div>
          <div className="commit-tips">
            <h4>Bons missatges de commit:</h4>
            <div className="commit-examples">
              <div className="commit-ex good">
                <span className="ex-icon">&#10003;</span>
                <code>"Afegir formulari de registre amb validacio"</code>
              </div>
              <div className="commit-ex good">
                <span className="ex-icon">&#10003;</span>
                <code>"Corregir error de login amb correu incorrecte"</code>
              </div>
              <div className="commit-ex bad">
                <span className="ex-icon">&#10007;</span>
                <code>"canvis"</code>
              </div>
              <div className="commit-ex bad">
                <span className="ex-icon">&#10007;</span>
                <code>"asdfgh"</code>
              </div>
              <div className="commit-ex bad">
                <span className="ex-icon">&#10007;</span>
                <code>"fix"</code>
              </div>
            </div>
          </div>
        </div>

        {/* Step 5: GitHub repo */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">5</div>
            <div>
              <h3>Crear repositori a GitHub</h3>
              <p>Ara crearem un repositori remot a GitHub per pujar-hi el codi.</p>
            </div>
          </div>
          <div className="github-steps">
            <div className="gh-step">
              <div className="gh-step-num">a</div>
              <div>
                <p>Ves a <a href="https://github.com/new" target="_blank" rel="noopener noreferrer">github.com/new</a></p>
              </div>
            </div>
            <div className="gh-step">
              <div className="gh-step-num">b</div>
              <div>
                <p>Omple el formulari:</p>
                <ul className="check-list">
                  <li><strong>Repository name:</strong> el-meu-projecte</li>
                  <li><strong>Description:</strong> El meu primer repositori Git</li>
                  <li><strong>Visibilitat:</strong> Public o Private</li>
                  <li><strong>NO</strong> marquis "Add a README file" (ja tenim fitxers locals)</li>
                </ul>
              </div>
            </div>
            <div className="gh-step">
              <div className="gh-step-num">c</div>
              <div><p>Clica <strong>"Create repository"</strong></p></div>
            </div>
          </div>
        </div>

        {/* Step 6: git push */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">6</div>
            <div>
              <h3>Connectar i pujar a GitHub</h3>
              <p>Connectem el repositori local amb el remot i pugem el codi.</p>
            </div>
          </div>
          <div className="tut-terminal">
            <div className="terminal-header">
              <span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span>
              <span className="terminal-title">terminal</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line comment"># Connectar amb el repositori remot de GitHub</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git remote add origin https://github.com/USUARI/el-meu-projecte.git</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Pujar el codi al remot (primer cop usa -u per vincular branques)</div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git push -u origin main</span></div>
              <div className="terminal-line output">Enumerating objects: 3, done.</div>
              <div className="terminal-line output">Counting objects: 100% (3/3), done.</div>
              <div className="terminal-line output">Writing objects: 100% (3/3), 234 bytes | 234.00 KiB/s, done.</div>
              <div className="terminal-line output success">Branch 'main' set up to track remote branch 'main' from 'origin'.</div>
            </div>
          </div>
          <div className="tut-explain">
            <strong>Fet!</strong> El teu codi ja es a GitHub. A partir d'ara, cada cop que facis canvis nomes necessites:
          </div>
          <div className="tut-terminal small">
            <div className="terminal-body">
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git add .</span></div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git commit -m "el teu missatge"</span></div>
              <div className="terminal-line"><span className="prompt">$</span><span className="cmd">git push</span></div>
            </div>
          </div>
        </div>

        {/* Step 7: gitignore */}
        <div className="tutorial-step">
          <div className="tut-step-header">
            <div className="tut-num">7</div>
            <div>
              <h3>El fitxer .gitignore</h3>
              <p>Alguns fitxers <strong>no s'han de pujar</strong> al repositori (contrasenyes, dependancies, fitxers temporals...).</p>
            </div>
          </div>
          <div className="tut-terminal">
            <div className="terminal-header">
              <span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span>
              <span className="terminal-title">.gitignore</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line comment"># Dependancies</div>
              <div className="terminal-line"><span className="file-content">node_modules/</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Fitxers de configuracio local</div>
              <div className="terminal-line"><span className="file-content">.env</span></div>
              <div className="terminal-line"><span className="file-content">.env.local</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Fitxers de IDE</div>
              <div className="terminal-line"><span className="file-content">.idea/</span></div>
              <div className="terminal-line"><span className="file-content">.vscode/</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Fitxers del sistema operatiu</div>
              <div className="terminal-line"><span className="file-content">.DS_Store</span></div>
              <div className="terminal-line"><span className="file-content">Thumbs.db</span></div>
              <div className="terminal-line spacer"></div>
              <div className="terminal-line comment"># Fitxers compilats</div>
              <div className="terminal-line"><span className="file-content">*.class</span></div>
              <div className="terminal-line"><span className="file-content">dist/</span></div>
              <div className="terminal-line"><span className="file-content">build/</span></div>
            </div>
          </div>
          <div className="warning-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <p><strong>MAI pugis contrasenyes, tokens o fitxers .env al repositori!</strong> Un cop pujats, queden a l'historial per sempre, encara que els esborris despres.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FirstRepo;
